import { ChangeEvent, useContext, useState } from 'react';
import { ALL_FILTERS } from 'utils/constants/Filters';
import { ColumnContext, FilterContext } from 'utils/context';

import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Spinner,
} from '@project44/manifest-ui-deprecated';
import { Modal } from '@project44/material-ui-deprecated';
import { useTranslation } from '@project44/translations-utils';
import {
  IBasicInventoryOrderSearchCriteria,
  IShipmentSearchCriteria,
  SavedView,
} from '@project44-models/shipment-list';

const styles = {
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  root: {
    bgColor: 'white',
    borderRadius: '8px',
    width: '350px',
  },
  body: {
    borderBottom: '1px solid',
    borderTop: '1px solid',
    borderColor: 'neutral.200',
    padding: '24px 20px',
    textAlign: 'left',
    overflowWrap: 'break-word',
  },
  icon: {
    borderRadius: '6px',
    height: '12px',
    width: '12px',
  },
};

interface ModalConfig {
  heading: string;
  buttonText: string;
  buttonColor?: 'danger' | 'primary' | 'neutral';
  isDisabled?: boolean;
  onConfirm: () => void;
  body: string | React.ReactElement;
}

interface ViewModalProps {
  createView: (body: SavedView) => Promise<{ views: SavedView[] }>;
  deleteView: (id: string) => Promise<{ views: SavedView[] }>;
  doSearch: () => void;
  getViewRequest: () =>
    | IShipmentSearchCriteria
    | IBasicInventoryOrderSearchCriteria;
  isLoading: boolean;
  onClose: () => void;
  revertView: () => void;
  selectedView?: SavedView;
  updateView: (id: string, body: SavedView) => Promise<{ views: SavedView[] }>;
  variant: 'create' | 'delete' | 'revert' | 'update';
  viewType: 'order' | 'shipment';
}

function ViewModal({
  createView,
  deleteView,
  doSearch,
  getViewRequest,
  isLoading,
  onClose,
  revertView,
  selectedView,
  updateView,
  variant,
  viewType,
}: ViewModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(selectedView?.name || '');
  const { clearFilters, setFiltersKey } = useContext(FilterContext);
  const { displayOptions } = useContext(ColumnContext);
  const isOrder = viewType === 'order';

  const handleClose = () => {
    setName('');
    onClose();
  };

  const config: {
    create: ModalConfig;
    delete: ModalConfig;
    revert: ModalConfig;
    update: ModalConfig;
  } = {
    create: {
      heading: t('core.createNewView'),
      buttonText: t('global.create'),
      isDisabled: !name,
      onConfirm: async () => {
        const filterCriteria = getViewRequest();
        const res = await createView({
          name,
          filterCriteria,
          displayOptions: displayOptions,
        });
        if (res?.views?.length && res.views[0].id) {
          setFiltersKey('listView', res.views[0].id);
        }
        handleClose();
      },
      body: (
        <Input
          isDisabled={isLoading}
          maxLength={40}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setName(event.target.value);
          }}
          placeholder={t('core.viewName')}
          startIcon={
            <Box
              sx={{
                ...styles.icon,
                bgColor: isOrder ? 'fill.pink.500' : '#0072ec',
              }}
            />
          }
          value={name}
        />
      ),
    },
    revert: {
      heading: t('core.revertView'),
      buttonText: t('global.revert'),
      buttonColor: 'danger',
      body: t('core.revertViewBody', { viewName: selectedView?.name }),
      onConfirm: () => {
        revertView();
        onClose();
      },
    },
    update: {
      heading: t('core.updateView'),
      buttonText: t('global.update'),
      body: t('core.updateViewBody', { viewName: selectedView?.name }),
      onConfirm: async () => {
        if (selectedView?.id) {
          await updateView(selectedView.id, {
            id: selectedView.id,
            name: selectedView.name,
            filterCriteria: getViewRequest(),
            displayOptions: selectedView.displayOptions,
          });
          doSearch();
          handleClose();
        }
      },
    },
    delete: {
      heading: t('core.deleteView'),
      buttonColor: 'danger',
      buttonText: t('global.delete'),
      body: t('core.deleteViewBody', { viewName: selectedView?.name || '' }),
      onConfirm: async () => {
        if (selectedView?.id) {
          await deleteView(selectedView.id);
          setFiltersKey('listView', isOrder ? 'all-orders' : 'all-shipments');
          clearFilters(ALL_FILTERS);
          handleClose();
        }
      },
    },
  };

  const content = config[variant] || {};

  return (
    <Modal
      data-testid="view-modal"
      open={!!variant}
      disablePortal
      style={styles.modal}
    >
      <Box sx={styles.root}>
        <Heading
          variant="h5"
          isBold
          style={{ padding: '24px 20px', textAlign: 'left' }}
        >
          {content.heading}
        </Heading>
        <Box sx={styles.body}>{content.body}</Box>
        <Flex
          justify="flex-end"
          align="center"
          sx={{ gap: 2, padding: '16px 20px' }}
        >
          {isLoading && <Spinner data-testid="loading" />}
          <Button
            variant="outlined"
            onClick={handleClose}
            isDisabled={isLoading}
          >
            {t('global.cancel')}
          </Button>
          <Button
            colorTheme={content?.buttonColor}
            onClick={content.onConfirm}
            isDisabled={isLoading || content.isDisabled}
          >
            {content.buttonText}
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
}

export default ViewModal;
